// A workspace's standing room link (host only): turn it on, copy it, start a session through it,
// set its waiting room and "files between sessions", or replace it with a new link.
import { useEffect, useState } from 'react';
import { api } from '../api';
import { T, t } from '../tenant';
import { tip } from '../guide';

interface RoomState {
  room: { path: string; waitingRoom: boolean; filesBetween: boolean } | null;
  live: { id: string; title: string } | null;
}

export async function startSession(workspaceId: string) {
  const r = await api<{ id: string }>(`/api/workspaces/${workspaceId}/room/start`, { body: {} });
  location.href = `/m/${r.id}`;
}

export function RoomPanel({ workspaceId }: { workspaceId: string }) {
  const [state, setState] = useState<RoomState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const load = () => api<RoomState>(`/api/workspaces/${workspaceId}/room`).then(setState, (e: Error) => setError(e.message));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);
  const act = (fn: () => Promise<unknown>) =>
    fn().then(
      () => (setError(null), load()),
      (e: Error) => setError(e.message),
    );
  if (!state) return error ? <div className="error">{error}</div> : null;
  const url = state.room ? `${location.origin}${state.room.path}` : '';

  return (
    <section className="room-panel list" data-testid="room-panel">
      <div className="row">
        <h2 className="h2" style={{ flex: 1, margin: 0 }}>
          {T.room}
        </h2>
        {state.room && (
          <button className="btn primary" onClick={() => act(() => startSession(workspaceId))} data-testid="start-session">
            {state.live ? `Rejoin the live ${t.meeting}` : `Start ${t.meeting}`}
          </button>
        )}
      </div>
      {error && <div className="error">{error}</div>}
      {!state.room ? (
        <>
          <p className="muted small" style={{ margin: 0 }}>
            One link that stays the same every time: send it once, and {t.guests} open it whenever there is a {t.meeting}. Before you start, the page tells them when the next one
            is.
          </p>
          <button className="btn primary" style={{ alignSelf: 'flex-start' }} onClick={() => act(() => api(`/api/workspaces/${workspaceId}/room`, { body: { waitingRoom: true } }))} data-testid="room-on">
            Turn on the {t.room}
          </button>
        </>
      ) : (
        <>
          <div className="linkbox">
            <input className="field" readOnly value={url} onFocus={(e) => e.currentTarget.select()} aria-label={T.room} data-testid="room-link" />
            <button
              className="btn"
              onClick={async () => {
                await navigator.clipboard?.writeText(url).catch(() => {});
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {state.live && <div className="notice small">A {t.meeting} is live now. {T.guests} who open the link join it (through the waiting room if it is on).</div>}
          <label className="row small">
            <input type="checkbox" checked={state.room.waitingRoom} onChange={(e) => act(() => api(`/api/workspaces/${workspaceId}/room`, { body: { waitingRoom: e.target.checked } }))} /> Waiting room: I let
            each {t.guest} in
          </label>
          <label className="row small">
            <input
              type="checkbox"
              checked={state.room.filesBetween}
              onChange={(e) => act(() => api(`/api/workspaces/${workspaceId}/room`, { body: { filesBetween: e.target.checked } }))}
              data-testid="files-between"
            />{' '}
            Anyone with the link can open the current files between {t.meetings} (homework, notes)
          </label>
          <div className="row">
            <button className="btn ghost sm" onClick={() => act(() => api(`/api/workspaces/${workspaceId}/room/rotate`, { body: {} }))} {...tip('Use this if the link reached the wrong chat. Send pupils the new link.')}>
              Make a new link (the old one stops working)
            </button>
            {confirmOff ? (
              <button className="btn danger sm" onClick={() => act(() => api(`/api/workspaces/${workspaceId}/room`, { method: 'DELETE' })).then(() => setConfirmOff(false))}>
                Confirm: turn the link off
              </button>
            ) : (
              <button className="btn ghost sm" onClick={() => setConfirmOff(true)}>
                Turn off
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
